import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Typography } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getUserList } from "../../Actions/UserAction";
import { globalStyle } from "../../global/Utils";

const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

const getPackage = (user) => {
  const invoices = user?.allowInvoices || 0;
  if (invoices > 200) return { label: "Premium", className: "bg-violet-100 text-violet-600" };
  if (invoices > 50) return { label: "Standard", className: "bg-sky-100 text-sky-600" };
  return { label: "Basic", className: "bg-slate-100 text-slate-500" };
};

const getStatus = (expireDate) => {
  if (!expireDate) return "Active";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expire = new Date(expireDate);
  expire.setHours(0, 0, 0, 0);
  if (expire < today) return "Expired";
  if (expire - today <= FOURTEEN_DAYS_MS) return "Need Renewal";
  return "Active";
};

const getTimeline = (expireDate) => {
  if (!expireDate) return "-";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expire = new Date(expireDate);
  expire.setHours(0, 0, 0, 0);
  const diffDays = Math.round((expire - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} ago`;
  if (diffDays === 0) return "Today";
  return `in ${diffDays} day${diffDays !== 1 ? "s" : ""}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0] || "").join("").toUpperCase().slice(0, 2) || "?";

const AVATAR_COLORS = ["#1a284f", "#1e40af", "#7c3aed", "#0f766e", "#b45309", "#be123c"];
const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash += name.charCodeAt(i);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const TABS = ["Expired", "Need Renewal", "All"];

const SubscriptionStatus = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Expired");

  const userDetails = useSelector((state) => state?.HeaderReducer?.userDetails);
  const { isGettingUserList, userList } = useSelector((state) => state?.UserReducer);

  useEffect(() => {
    dispatch(getUserList("", "", "", "customer"));
  }, [dispatch]);

  if (!userDetails) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <CircularProgress />
      </div>
    );
  }

  if (userDetails.role !== "admin") {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <Typography className="text-slate-500">
          Access denied. This page is for administrators only.
        </Typography>
      </div>
    );
  }

  if (isGettingUserList && (!userList || userList.length === 0)) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <CircularProgress />
      </div>
    );
  }

  const customers = (userList || []).filter((u) => u.role !== "admin");

  const usersWithStatus = customers.map((user) => ({
    ...user,
    _status: getStatus(user.expireDate),
    _package: getPackage(user),
    _timeline: getTimeline(user.expireDate),
  }));

  const expiredList = usersWithStatus.filter((u) => u._status === "Expired");
  const needRenewalList = usersWithStatus.filter((u) => u._status === "Need Renewal");
  const activeList = usersWithStatus.filter((u) => u._status === "Active");

  const tabData = {
    Expired: expiredList,
    "Need Renewal": needRenewalList,
    All: usersWithStatus,
  };

  const tabCounts = {
    Expired: expiredList.length,
    "Need Renewal": needRenewalList.length,
    All: usersWithStatus.length,
  };

  const currentData = tabData[activeTab] || [];

  return (
    <div className="min-h-screen p-6 bg-[#F4F5F6]">
      {/* Page Header */}
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-[#1a284f]"
          style={{ fontFamily: globalStyle.fontFamilyHeading }}
        >
          Subscription Status
        </h1>
        <p
          className="text-sm mt-1 text-slate-500"
          style={{ fontFamily: globalStyle.fontFamilyParagraph }}
        >
          Accounts needing attention, sorted by urgency.
        </p>
      </div>

      {/* Alert Banner */}
      {(expiredList.length > 0 || needRenewalList.length > 0) && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-5 py-3.5 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <WarningAmberIcon className="shrink-0 text-red-600" sx={{ fontSize: 28 }} />
            <div>
              <p
                className="font-bold text-red-600 text-[15px] m-0"
                style={{ fontFamily: globalStyle.fontFamilyHeading }}
              >
                {expiredList.length > 0 &&
                  `${expiredList.length} subscription${expiredList.length !== 1 ? "s" : ""} have expired`}
                {expiredList.length > 0 && needRenewalList.length > 0 && " and "}
                {needRenewalList.length > 0 &&
                  `${needRenewalList.length} will expire within 14 days.`}
              </p>
              <p
                className="text-red-500 text-[13px] m-0"
                style={{ fontFamily: globalStyle.fontFamilyParagraph }}
              >
                Renew or send reminders to keep these accounts active.
              </p>
            </div>
          </div>
          <button
            onClick={() => setActiveTab("Expired")}
            className="bg-red-600 text-white border-0 rounded-md px-[18px] py-2 font-semibold cursor-pointer whitespace-nowrap text-sm"
            style={{ fontFamily: globalStyle.fontFamilyHeading }}
          >
            Review expired
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {/* Expired */}
        <div className="rounded-xl shadow p-5 flex items-center gap-4 bg-white">
          <div className="bg-red-100 rounded-full w-12 h-12 flex items-center justify-center shrink-0">
            <CancelIcon sx={{ color: "#dc2626" }} />
          </div>
          <div>
            <p
              className="text-[30px] font-bold m-0 leading-none"
              style={{ fontFamily: globalStyle.fontFamilyHeading }}
            >
              {expiredList.length}
            </p>
            <p
              className="text-sm text-slate-500 mt-1 m-0"
              style={{ fontFamily: globalStyle.fontFamilyParagraph }}
            >
              Expired
            </p>
          </div>
        </div>

        {/* Need Renewal */}
        <div className="rounded-xl shadow p-5 flex items-center gap-4 bg-white">
          <div className="bg-amber-100 rounded-full w-12 h-12 flex items-center justify-center shrink-0">
            <AccessTimeIcon sx={{ color: "#d97706" }} />
          </div>
          <div>
            <p
              className="text-[30px] font-bold m-0 leading-none"
              style={{ fontFamily: globalStyle.fontFamilyHeading }}
            >
              {needRenewalList.length}
            </p>
            <p
              className="text-sm text-slate-500 mt-1 m-0"
              style={{ fontFamily: globalStyle.fontFamilyParagraph }}
            >
              Need renewal (≤14 days)
            </p>
          </div>
        </div>

        {/* Active */}
        <div className="rounded-xl shadow p-5 flex items-center gap-4 bg-white">
          <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center shrink-0">
            <CheckCircleIcon sx={{ color: "#16a34a" }} />
          </div>
          <div>
            <p
              className="text-[30px] font-bold m-0 leading-none"
              style={{ fontFamily: globalStyle.fontFamilyHeading }}
            >
              {activeList.length}
            </p>
            <p
              className="text-sm text-slate-500 mt-1 m-0"
              style={{ fontFamily: globalStyle.fontFamilyParagraph }}
            >
              Active subscriptions
            </p>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="rounded-xl shadow-xl overflow-hidden bg-white">
        {/* Tabs */}
        <div className="border-b border-slate-200 flex px-4">
          {TABS.map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`bg-transparent border-0 border-b-2 px-4 py-3.5 cursor-pointer text-sm flex items-center gap-1.5 whitespace-nowrap transition-colors ${
                  isActive
                    ? "border-b-[#3A80FB] text-[#3A80FB] font-semibold"
                    : "border-b-transparent text-slate-500 font-normal"
                }`}
                style={{ fontFamily: globalStyle.fontFamilyHeading }}
              >
                {tab}
                <span
                  className={`rounded-full text-[11px] px-[7px] py-px font-semibold ${
                    isActive ? "bg-[#3A80FB] text-white" : "bg-slate-200 text-slate-500"
                  }`}
                >
                  {tabCounts[tab]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[600px]">
            <thead>
              <tr className="bg-[#3A80FB]">
                {["Account", "Package", "Expiry date", "Timeline", "Status"].map((col) => (
                  <th
                    key={col}
                    className="px-4 py-3 text-white font-semibold text-sm text-left whitespace-nowrap"
                    style={{ fontFamily: globalStyle.fontFamilyHeading }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentData.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="text-center px-4 py-10 text-slate-400 text-sm"
                    style={{ fontFamily: globalStyle.fontFamilyParagraph }}
                  >
                    No accounts in this category.
                  </td>
                </tr>
              ) : (
                currentData.map((user, index) => {
                  const isExpired = user._status === "Expired";
                  const isNeedRenewal = user._status === "Need Renewal";
                  const pkg = user._package;

                  return (
                    <tr
                      key={user._id || index}
                      className={`border-b border-b-slate-100 border-l-[3px] ${
                        isExpired
                          ? "border-l-red-600"
                          : isNeedRenewal
                          ? "border-l-amber-600"
                          : "border-l-transparent"
                      } ${index % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                    >
                      {/* Account */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-9 h-9 rounded-full text-white flex items-center justify-center font-bold text-[13px] shrink-0"
                            style={{
                              background: getAvatarColor(user.userName || ""),
                              fontFamily: globalStyle.fontFamilyHeading,
                            }}
                          >
                            {getInitials(user.userName)}
                          </div>
                          <span
                            className="font-medium text-slate-800 text-sm"
                            style={{ fontFamily: globalStyle.fontFamilyParagraph }}
                          >
                            {user.userName || "-"}
                          </span>
                        </div>
                      </td>

                      {/* Package */}
                      <td className="px-4 py-3">
                        <span
                          className={`rounded px-2.5 py-[3px] text-xs font-semibold ${pkg.className}`}
                          style={{ fontFamily: globalStyle.fontFamilyHeading }}
                        >
                          {pkg.label}
                        </span>
                      </td>

                      {/* Expiry Date */}
                      <td
                        className="px-4 py-3 text-sm text-slate-600"
                        style={{ fontFamily: globalStyle.fontFamilyParagraph }}
                      >
                        {formatDate(user.expireDate)}
                      </td>

                      {/* Timeline */}
                      <td className="px-4 py-3">
                        <span
                          className={`font-semibold text-sm ${
                            isExpired
                              ? "text-red-600"
                              : isNeedRenewal
                              ? "text-amber-600"
                              : "text-green-600"
                          }`}
                          style={{ fontFamily: globalStyle.fontFamilyParagraph }}
                        >
                          {user._timeline}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold inline-flex items-center gap-[5px] ${
                            isExpired
                              ? "bg-red-100 text-red-600"
                              : isNeedRenewal
                              ? "bg-amber-100 text-amber-600"
                              : "bg-green-100 text-green-600"
                          }`}
                          style={{ fontFamily: globalStyle.fontFamilyHeading }}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full inline-block shrink-0 ${
                              isExpired
                                ? "bg-red-600"
                                : isNeedRenewal
                                ? "bg-amber-600"
                                : "bg-green-600"
                            }`}
                          />
                          {user._status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionStatus;
